import { THEMES, NAVY, ACCENT } from "./ThemeContext";

// Shared pieces of the project manager view (Ülevaade and Pakkumine tabs). Colours come from the app theme
// (ThemeContext), so the view looks like the dashboard and the other pages in both light and dark mode.
const fromTheme = (t, dark) => ({
  bg: t.bg, card: t.panel, cardBorder: t.border, rowBorder: t.rowBorder, head: t.tableHead,
  text: t.text, label: t.muted, muted: t.muted, mutedLight: t.mutedLight, soft: dark ? "#c3c9d6" : "#4b5563", track: t.border,
  accent: ACCENT, onAccent: "#fff", pill: `color-mix(in srgb, ${ACCENT} 16%, ${t.panel})`, machBar: dark ? "#c3c9d6" : NAVY,
  ctl: t.bg, ctlBorder: t.border, inBg: t.bg, inBorder: t.border,
  warn: "#dd6b20", warnSoft: "#dd6b2018", error: t.error, errorSoft: dark ? "#3a1c1c" : "#fdecea",
  shadow: "0 1px 2px rgba(0,0,0,0.06)", overlay: "rgba(0,0,0,0.45)",
});

export const PALETTE = { dark: fromTheme(THEMES.dark, true), light: fromTheme(THEMES.light, false) };

// Section and card titles, as on the dashboard's source cards.
export const labelStyle = c => ({ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.muted });

export function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(String(v).replace(",", ".").replace(/\s/g, ""));
  return isFinite(n) ? n : null;
}

export function fmt(n, d = 0) {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  const [whole, dec] = Math.abs(n).toFixed(d).split(".");
  return (n < 0 ? "−" : "") + whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + (dec ? "." + dec : "");
}

export const str = v => (v === null || v === undefined ? "" : String(v));

export function Chevron({ open, color }) {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .15s" }}>
      <path d="M3 5l4 4 4-4" />
    </svg>
  );
}

export function Seg({ c, items, value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {items.map(it => {
        const on = it.key === value;
        return (
          <button key={it.key} onClick={() => !it.disabled && onChange(it.key)} disabled={it.disabled} title={it.title}
            style={{
              height: 34, padding: "0 14px", fontSize: 13, fontWeight: on ? 700 : 500, borderRadius: 8,
              color: on ? c.accent : c.muted, background: on ? c.accent + "18" : "transparent",
              border: `1px solid ${on ? c.accent + "44" : c.cardBorder}`,
              cursor: it.disabled ? "default" : "pointer", opacity: it.disabled ? 0.5 : 1, fontFamily: "inherit",
            }}>{it.label}</button>
        );
      })}
    </div>
  );
}

export function Pill({ c, children }) {
  return (
    <span style={{ fontSize: 13, fontWeight: 600, color: c.accent, background: c.pill, padding: "6px 12px", borderRadius: 999, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

export function EditButton({ c, editing, saving, onClick }) {
  return (
    <button onClick={onClick} aria-pressed={editing} disabled={saving} style={{
      height: 40, padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600,
      borderRadius: 8, border: `1px solid ${editing ? c.accent : c.inBorder}`, background: editing ? c.accent : "transparent",
      color: editing ? c.onAccent : c.soft, cursor: "pointer", fontFamily: "inherit",
    }}>
      {editing
        ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.5 7.5l3 3 6-6.5" /></svg>
        : <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9.5 2.5l2 2M2.5 11.5l.5-2.5 6.5-6.5 2 2-6.5 6.5-2.5.5z" /></svg>}
      <span>{saving ? "Salvestan…" : editing ? "Salvesta" : "Muuda"}</span>
    </button>
  );
}

export function Cell({ c, editing, value, onChange, label, align = "right", placeholder }) {
  if (!editing) {
    return <div style={{ textAlign: align, fontSize: 15, fontWeight: 600, padding: "0 11px" }}>{value === "" ? "—" : value}</div>;
  }
  return (
    <input type="text" inputMode={align === "right" ? "decimal" : "text"} value={value} onChange={e => onChange(e.target.value)}
      aria-label={label} placeholder={placeholder}
      style={{
        width: "100%", height: 44, boxSizing: "border-box", padding: "0 10px", textAlign: align, fontSize: 15, fontWeight: 600,
        background: c.inBg, border: `1px solid ${c.inBorder}`, borderRadius: 8, color: c.text, fontFamily: "inherit",
      }} />
  );
}

export function Section({ c, title, open, onToggle, actions, pill, children }) {
  return (
    <section style={{ background: c.card, border: `1px solid ${c.cardBorder}`, borderRadius: 14, overflow: "hidden", boxShadow: c.shadow }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 24px", flexWrap: "wrap" }}>
        <button onClick={onToggle} aria-expanded={open} style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, minHeight: 40 }}>
          <Chevron open={open} color={c.accent} />
          <span style={labelStyle(c)}>{title}</span>
        </button>
        {actions}
        <span style={{ flexGrow: 1 }} />
        {pill}
      </div>
      {open && <div style={{ overflowX: "auto" }}>{children}</div>}
    </section>
  );
}

